import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUsers } from "../../context/UsersContext";
import { FaArrowLeft, FaSpinner } from "react-icons/fa";
import Notification from "../Notification";

const TeacherForm = () => {
  const { id } = useParams(); // Obtener el ID de la URL
  const [formData, setFormData] = useState({
    first_name: "",
    second_name: "",
    first_surname: "",
    second_surname: "",
    correo: "",
    celular: "",
    numero_identificacion: "",
    fecha_nacimiento: "",
    image: null,
    director_salon: "",
    asignaciones: [],
  });
  const [asignaciones, setAsignaciones] = useState([]);

  const {
    createNewTeacher,
    updateTeacherData,
    getUser,
    fetchSalones,
    fetchAsignaturas,
    salones,
    asignaturas,
  } = useUsers();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // Nuevo estado de carga
  const [notification, setNotification] = useState(null);
  
  const closeNotification = () => setNotification(null);

  // ✅ FUNCIÓN para convertir DD-MM-YYYY a YYYY-MM-DD
  const convertirFechaParaInput = (fecha) => {
    if (!fecha) return "";
    const partes = fecha.split("-");
    if (partes.length === 3) {
      const [dia, mes, año] = partes;
      return `${año}-${mes.padStart(2, "0")}-${dia.padStart(2, "0")}`;
    }
    return fecha;
  };

  // ✅ FUNCIÓN para convertir YYYY-MM-DD a DD-MM-YYYY
  const convertirFechaParaBackend = (fecha) => {
    if (!fecha) return "";
    const partes = fecha.split("-");
    if (partes.length === 3) {
      const [año, mes, dia] = partes;
      return `${dia}-${mes}-${año}`;
    }
    return fecha;
  };

  // Memorizar las funciones para evitar dependencias innecesarias en el useEffect
  const fetchSalonesMemo = useCallback(fetchSalones, [fetchSalones]);
  const fetchAsignaturasMemo = useCallback(fetchAsignaturas, [
    fetchAsignaturas,
  ]);
  const getUserMemo = useCallback(getUser, [getUser]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (salones.length === 0) {
          await fetchSalonesMemo();
        }
        if (asignaturas.length === 0) {
          await fetchAsignaturasMemo();
        }

        if (id) {
          const teacher = await getUserMemo(id);
          setFormData({
            first_name: teacher.first_name || "",
            second_name: teacher.second_name || "",
            first_surname: teacher.first_surname || "",
            second_surname: teacher.second_surname || "",
            correo: teacher.correo || "",
            celular: teacher.celular || "",
            numero_identificacion: teacher.numero_identificacion || "",
            fecha_nacimiento: convertirFechaParaInput(teacher.fecha_nacimiento) || "", // ✅ Convertir formato
            image: null,
            director_salon: teacher.director_salon || "",
            asignaciones: teacher.asignaciones || [],
          });
          setAsignaciones(teacher.asignaciones || []);
        }
      } catch (error) {
      }
    };

    fetchInitialData();
  }, [
    id,
    salones.length,
    asignaturas.length,
    fetchSalonesMemo,
    fetchAsignaturasMemo,
    getUserMemo,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file); // Generar URL temporal
      setFormData((prev) => ({ ...prev, image: file, previewUrl })); // Agregar `previewUrl` al estado
    }
  };

  // 🔹 Validar formato DD-MM-YYYY
  const validarFecha = (fecha) => {
    const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (!regex.test(fecha)) return false;

    const [dia, mes, año] = fecha.split("-").map(Number);
    if (mes < 1 || mes > 12) return false;
    if (dia < 1 || dia > 31) return false;
    if (año < 1900 || año > new Date().getFullYear()) return false;

    return true;
  };

  const addAsignacion = () => {
    setAsignaciones((prev) => [...prev, { asignatura_id: "", salon_id: "" }]);
  };

  const updateAsignacion = (index, field, value) => {
    setAsignaciones((prev) =>
      prev.map((asignacion, i) =>
        i === index ? { ...asignacion, [field]: value } : asignacion
      )
    );
  };

  const removeAsignacion = (index) => {
    setAsignaciones((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const data = new FormData();
      for (const key in formData) {
        if (key === "director_salon" && formData[key] === "") {
          data.append(key, null); // Enviar null si está vacío
        } else if (key === "asignaciones") {
          data.append(
            key,
            JSON.stringify(asignaciones.length > 0 ? asignaciones : [])
          );
        } else {
          data.append(key, formData[key]);
        }
      }

      if (id) {
        await updateTeacherData(id, data);
      } else {
        await createNewTeacher(data);
      }
      navigate("/profesores");
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen flex flex-col items-center p-6">
      <button
        className="self-start flex items-center bg-main-bg-sidebar rounded-full hover:bg-blue-800 text-white font-bold  px-5 py-3 mb-4"
        onClick={() => navigate(-1)}
      >
        <FaArrowLeft className="mr-2 " />
        Volver
      </button>
      <div className="w-full max-w-3xl">
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-lg rounded-xl p-8 space-y-8"
        >
          <h2 className="bg-main-bg-sidebar text-white flex justify-between items-center px-6 py-2 rounded-t-lg font-bold w-full">
            Información Personal
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Primer Nombre *
              </label>
              <input
                type="text"
                name="first_name"
                placeholder="Primer Nombre"
                value={formData.first_name}
                onChange={handleInputChange}
                required
                className="border-gray border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>
            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Segundo Nombre
              </label>
              <input
                type="text"
                name="second_name"
                placeholder="Segundo Nombre"
                value={formData.second_name}
                onChange={handleInputChange}
                className="border-gray border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>

            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Primer Apellido *
              </label>
              <input
                type="text"
                name="first_surname"
                placeholder="Primer Apellido"
                value={formData.first_surname}
                onChange={handleInputChange}
                required
                className="border-gray border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>

            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Segundo Apellido *
              </label>
              <input
                type="text"
                name="second_surname"
                placeholder="Segundo Apellido"
                value={formData.second_surname}
                onChange={handleInputChange}
                required
                className="border-gray border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>

            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Correo *
              </label>
              <input
                type="email"
                name="correo"
                placeholder="Correo"
                value={formData.correo}
                onChange={handleInputChange}
                required
                className="border-gray border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>

            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Celular *
              </label>
              <input
                type="text"
                name="celular"
                placeholder="Celular"
                value={formData.celular}
                onChange={handleInputChange}
                required
                className="border-gray border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>

            {/* 🔹 NUEVO CAMPO - Número de Identificación */}
            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Número de Identificación
              </label>
              <input
                type="text"
                name="numero_identificacion"
                placeholder="TI/CC (Opcional)"
                value={formData.numero_identificacion}
                onChange={handleInputChange}
                className="border-gray border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>

            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Fecha de Nacimiento *
              </label>
              <input
                type="date"
                name="fecha_nacimiento"
                value={formData.fecha_nacimiento}
                onChange={handleInputChange}
                placeholder="15-03-2010"
                pattern="\d{2}-\d{2}-\d{4}"
                title="Formato: DD-MM-YYYY"
                required
                className="border-gray border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              />
            </div>

            <div className="flex flex-col items-center justify-center p-6 w-full md:col-span-2">
              <label className="block text-blue-900 font-semibold mb-2">
                Foto *
              </label>
              <input
                type="file"
                name="image"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer border-2 border-dashed border-gray rounded-lg p-6 w-full text-center text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {formData.previewUrl ? (
                  <img
                    src={formData.previewUrl}
                    alt="Preview"
                    className="max-h-40 mx-auto"
                  />
                ) : (
                  "Subir Foto"
                )}
              </label>
            </div>

            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Director de Salón *
              </label>
              <select
                name="director_salon"
                value={formData.director_salon}
                onChange={handleInputChange}
                className="border-gray-300 border rounded-lg p-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-800"
              >
                <option value="">No Asignado</option>{" "}
                {/* Opción predeterminada */}
                {salones.map((salon) => (
                  <option key={salon.id} value={salon.id}>
                    {salon.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Campo de Asignaciones */}
            <div>
              <label className="block text-blue-900 font-semibold mb-2">
                Asignaciones *
              </label>
              {asignaciones.map((asignacion, index) => {
                // Obtener el salón seleccionado
                const salonSeleccionado = salones.find(
                  (salon) => salon.id === parseInt(asignacion.salon_id)
                );

                return (
                  <div key={index} className="flex space-x-4 mb-2">
                    {/* Selección de Salón */}
                    <select
                      value={asignacion.salon_id}
                      onChange={(e) =>
                        updateAsignacion(index, "salon_id", e.target.value)
                      }
                      className="border p-3 rounded-2xl w-full"
                    >
                      <option value="">Seleccione un salón</option>
                      {salones.map((salon) => (
                        <option key={salon.id} value={salon.id}>
                          {salon.nombre}
                        </option>
                      ))}
                    </select>

                    {/* Selección de Asignatura (solo si hay un salón seleccionado) */}
                    {asignacion.salon_id && (
                      <select
                        value={asignacion.asignatura_id}
                        onChange={(e) =>
                          updateAsignacion(
                            index,
                            "asignatura_id",
                            e.target.value
                          )
                        }
                        className="border p-3 rounded-2xl w-full"
                      >
                        <option value="">Seleccione una asignatura</option>
                        {asignaturas
                          .filter((asignatura) => {
                            const dimensionesPermitidas = [
                              "Cognoscitiva",
                              "Comunicativa",
                              "Espiritual",
                              "Estetica",
                              "Corporal",
                            ];
                            const esDimensionValida =
                              dimensionesPermitidas.some((dim) =>
                                asignatura.descripcion.includes(dim)
                              );
                            const esGradoPreescolar = salonSeleccionado
                              ? [
                                  "parvulos",
                                  "prejardin",
                                  "jardin",
                                  "transicion",
                                ].includes(
                                  salonSeleccionado.grado.toLowerCase()
                                )
                              : false;
                            return esGradoPreescolar
                              ? esDimensionValida
                              : !esDimensionValida;
                          })
                          .map((asignatura) => (
                            <option key={asignatura.id} value={asignatura.id}>
                              {asignatura.nombre}
                            </option>
                          ))}
                      </select>
                    )}

                    {/* Botón para eliminar asignación */}
                    <button
                      type="button"
                      onClick={() => removeAsignacion(index)}
                      className="bg-red-700 hover:bg-red-800 text-white rounded-full px-4 text-sm"
                    >
                      Eliminar
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={addAsignacion}
                className="bg-blue-500 rounded-full hover:bg-blue-800 text-white px-5 py-2"
              >
                Agregar Asignación
              </button>
            </div>
          </div>

          <div className="text-right">
            <button
              type="submit"
              className="bg-main-bg-sidebar rounded-full hover:bg-blue-800 text-white font-bold  px-6 py-3  focus:outline-none focus:ring-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <FaSpinner className="animate-spin mr-2" />
              ) : (
                "Guardar"
              )}
            </button>
          </div>
        </form>
      </div>
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={closeNotification}
        />
      )}
    </div>
  );
};

export default TeacherForm;
